/**
 * Paddle.js v2 (the overlay checkout), loaded only when the subscribe page needs it. The website
 * passes its token and environment through the platform (`platform.paddle`); a sandbox token
 * never reaches production and the other way round (see packages/app/src/env.ts).
 */

export interface PaddleConfig {
  token: string;
  environment: 'sandbox' | 'production';
}

export interface PaddleEvent {
  name?: string;
}

interface PaddleGlobal {
  Environment: { set(environment: 'sandbox' | 'production'): void };
  Initialize(options: {
    token: string;
    eventCallback?: (event: PaddleEvent) => void;
    checkout?: { settings?: Record<string, unknown> };
  }): void;
  Checkout: { open(options: { transactionId: string; settings?: Record<string, unknown> }): void };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

export const PADDLE_SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

export interface CheckoutHandlers {
  onCompleted: () => void;
  onClosed?: () => void;
}

let loading: Promise<PaddleGlobal> | null = null;
let initializedWith: string | null = null;
let handlers: CheckoutHandlers | null = null;

function loadPaddle(): Promise<PaddleGlobal> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  loading ??= new Promise<PaddleGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PADDLE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.Paddle) resolve(window.Paddle);
      else reject(new Error('Paddle did not start'));
    };
    script.onerror = () => {
      script.remove();
      loading = null; // a later click may try again
      reject(new Error('Paddle could not be loaded'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Opens Paddle's overlay checkout for a transaction the server made. */
export async function openPaddleCheckout(
  config: PaddleConfig,
  transactionId: string,
  checkoutHandlers: CheckoutHandlers
): Promise<void> {
  const paddle = await loadPaddle();
  handlers = checkoutHandlers;
  const key = `${config.environment}:${config.token}`;
  if (initializedWith !== key) {
    // Paddle.js wants the environment set before it is initialised.
    if (config.environment === 'sandbox') paddle.Environment.set('sandbox');
    paddle.Initialize({
      token: config.token,
      eventCallback: (event) => {
        if (event.name === 'checkout.completed') handlers?.onCompleted();
        else if (event.name === 'checkout.closed') handlers?.onClosed?.();
      },
      checkout: { settings: { displayMode: 'overlay', theme: 'light' } },
    });
    initializedWith = key;
  }
  paddle.Checkout.open({ transactionId });
}
